import React from 'react';
import { RouteChildrenProps } from 'react-router';
import { Grid, Header, Icon } from 'semantic-ui-react';

import { clienteApi, ErrorApi } from 'src/api';
import { Contexto } from 'src/contexto';
import { compararNombresConApellidosPrimero } from 'src/utilidades';
import { HeaderNombreDeCurso } from '../../componentes';
import { DetallesDelCurso, Profesor, Sala } from '../tipos';
import { InformacionBasica } from './InformacionBasica';
import { InformacionEditable } from './InformacionEditable';
import { ListaDeAlumnos } from './ListaDeAlumnos';
import { MenuDeListado } from './MenuDeListado';
import { ModalCorreos } from './ModalCorreos';

type Props = RouteChildrenProps<{}> &
    Readonly<{
        idCurso: number;
        actualizarDetalle(): Promise<DetallesDelCurso>;
    }>;

type State = Readonly<{
    curso?: DetallesDelCurso;
    salas: ReadonlyArray<Sala>;
    profesores: ReadonlyArray<Profesor>;
    error?: string;
    correosAMostrar?: readonly string[];
}>;

class Informacion extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state: State = {
        salas: [],
        profesores: [],
    };

    private readonly idCurso = this.props.idCurso;

    public componentDidMount() {
        this.obtenerCurso().catch(); // La función maneja sus errores
    }

    public render() {
        const { error, curso, correosAMostrar } = this.state;

        if (error) {
            return <Header as='h1'>{this.state.error}</Header>;
        }

        if (!curso) {
            return <Icon loading name='spinner' size='big' />;
        }

        return (
            <div>
                <HeaderNombreDeCurso curso={curso} />

                <Grid columns='equal'>
                    <Grid.Column width='6'>
                        <InformacionBasica curso={curso} />

                        <InformacionEditable
                            curso={curso}
                            salas={this.state.salas}
                            profesores={this.state.profesores}
                            alAsignarSobrecupo={this.asignarSobrecupo}
                            alAsignarProfesor={this.asignarProfesor}
                            alCambiarSala={this.cambiarDeSala}
                            alEliminarCurso={this.eliminarCurso}
                            alRequerirProfesores={this.obtenerProfesores}
                            alRequerirSalasDeDestino={this.obtenerSalasDeDestino}
                        />
                    </Grid.Column>

                    <Grid.Column>
                        <MenuDeListado
                            cantidadDeAlumno={curso.alumnos.length + curso.recuperaciones.length}
                            alPresionarCorreos={this.mostrarModalDeCorreos}
                        />

                        <ListaDeAlumnos curso={curso} />
                    </Grid.Column>
                </Grid>

                {correosAMostrar && (
                    <ModalCorreos
                        titulo='Correos electrónicos de todos los alumnos'
                        correos={correosAMostrar}
                        alCerrar={() => this.setState({ correosAMostrar: undefined })}
                    />
                )}
            </div>
        );
    }

    private readonly mostrarModalDeCorreos = () => {
        if (!this.state.curso) return;

        const correosDeAlumnos = this.state.curso.correos;

        this.setState({
            correosAMostrar: correosDeAlumnos.concat(
                this.state.curso.asistentes.map(asis => asis.correo),
            ),
        });
    };

    private readonly asignarSobrecupo = async (sobrecupo: number) => {
        return clienteApi
            .ejecutar('asignarSobrecupoAlCuso', {
                idCurso: this.idCurso,
                sobrecupo,
            })
            .then(() => this.obtenerCurso())
            .catch(this.context.mostrarModalError);
    };

    private readonly cambiarDeSala = async (idSala: string) => {
        return clienteApi
            .ejecutar('cambiarCursoDeSala', {
                idCurso: this.idCurso,
                idSala,
            })
            .then(() => this.setState({ salas: [] }))
            .then(() => this.obtenerCurso())
            .catch(this.context.mostrarModalError);
    };

    private readonly asignarProfesor = async (idProfesor: string) => {
        return clienteApi
            .ejecutar('asignarProfesorAlCurso', {
                idCurso: this.idCurso,
                idProfesor,
            })
            .then(() => this.obtenerCurso())
            .catch(this.context.mostrarModalError);
    };

    private readonly obtenerSalasDeDestino = () => {
        clienteApi
            .ejecutar('salasDeDestinoParaElCurso', this.idCurso)
            .then(({ salas }) => this.setState({ salas }))
            .catch(this.context.mostrarModalError);
    };

    private readonly obtenerProfesores = () => {
        this.setState({ profesores: [] });
        clienteApi
            .ejecutar('todosLosProfesores')
            .then(({ profesores }) => this.setState({ profesores }))
            .catch(this.context.mostrarModalError);
    };

    private readonly eliminarCurso = () => {
        clienteApi
            .ejecutar('eliminarCurso', this.idCurso)
            .then(() => this.props.history.push('/horarios/administrar/cursos'))
            .catch(this.context.mostrarModalError);
    };

    private readonly obtenerCurso = async () => {
        return this.props
            .actualizarDetalle()
            .then(async detalleDelCurso => {
                const curso = {
                    ...detalleDelCurso,
                    alumnos: detalleDelCurso.alumnos.sort((a, b) =>
                        compararNombresConApellidosPrimero(a.nombre, b.nombre),
                    ),
                    recuperaciones: detalleDelCurso.recuperaciones.sort((a, b) =>
                        compararNombresConApellidosPrimero(a.nombre, b.nombre),
                    ),
                };

                this.setState({
                    curso,
                    error: undefined,
                });
            })
            .catch(error => {
                this.context.mostrarModalError(error);
                if (error instanceof ErrorApi && error.tipo === 'VALIDACION') {
                    this.props.history.goBack();
                }
            });
    };
}

export default Informacion;



// WEBPACK FOOTER //
// ./src/modulos/Horarios/DetalleDelCurso/Informacion/index.tsx